/**
 * A recorded parameter's monitoring — the /process-config Monitoring dialog (mbmr/Monitoring.js)
 * on the step: the editor, its settings, and the Runtime Execution Rules shown under "Rules bound here".
 */
import React from "react";
import { Input, InputNumber, Select, Switch } from "antd";
import { Field } from "./Field";
import { MachineSidePicker } from "./machines";
import { CaptureEditor } from "./CaptureForm";


/** The facts on a placed row that the monitoring editor owns; shown once, in the editor. */
export const MON_OWNED_FACTS = new Set(["Acceptable range", "Frequency", "Out of range", "Correction attempts"]);

/** One sentence for how a parameter is watched, for the "why it is collected" list. */
export function monitoringSentence(m) {
  if (!m) return null;
  const range = m.min != null || m.max != null
    ? `judged against ${m.min != null ? m.min : "…"} – ${m.max != null ? m.max : "…"}${m.unit ? ` ${m.unit}` : ""}` : null;
  const when = m.schedule === "Periodic" && m.every ? `every ${m.every} ${m.every_unit || "min"}`
    : m.schedule === "Count" && m.readings ? `${m.readings} reading${Number(m.readings) === 1 ? "" : "s"}`
    : m.schedule === "Adhoc" ? "when the operator judges it needed" : null;
  const parts = [range, when, m.continuous ? "continuously" : null].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/** RV9o · how the parameter is watched. These are the facts on the `Process_Parameter` row —
 *  the row execution enforces — shown and edited here so the designer is where they are set.
 *  Staged into the step's draft under `monitoring` and saved with the rest of the step. */
export function MonitoringEditor({ step, readOnly, vocab, onStage, heading = true, split = false, unitHint = "", showErrors = false }) {
  // 2026-10-14 · the redesigned step panel (`split`) shows the core of the dialog — range,
  // schedule, readings, out of range, target — and keeps every other setting behind "Advanced".
  const [adv, setAdv] = React.useState(false);
  const m = step.monitoring || {};
  const words = vocab.monitoring || { schedules: [], out_of_range: [], interval_units: [] };
  // The whole monitoring object is staged, display facts included (unit, ceiling, code): the
  // server sets those aside and writes only what changed (services/recipe/monitoring.py).
  const patch = (delta) => onStage({ monitoring: { ...m, ...delta } });
  const schedule = m.schedule || "Periodic";
  const ceiling = m.ceiling;
  const unit = m.unit || ceiling?.unit || unitHint || "";
  return (
    <div className="u0-mon" data-testid="monitoring">
      {heading ? <span className="dz-eyebrow">How it is monitored</span> : null}
      {ceiling ? (
        <div className="u0-mon-ceiling">
          MFR limit {ceiling.min ?? "…"} – {ceiling.max ?? "…"}{ceiling.unit ? ` ${ceiling.unit}` : ""} for {ceiling.name} — the record may narrow it, never widen it.
        </div>
      ) : null}
      {/* 2026-10-30 (owner) · where the value comes from: Captured (machine tag) · Entered · Calculated. */}
      <CaptureEditor m={m} readOnly={readOnly} patch={patch} showErrors={showErrors} />
      <div className="u0-mon-grid">
        <Field label={split ? "Acceptable range" : `Acceptable range${unit ? ` (${unit})` : ""}`} hint="A reading outside it is out of range. The unit is the control master's; a limit typed in another unit is a limit nothing can judge.">
          <div className="u0-mon-pair" data-mon="range">
            <InputNumber size="small" value={m.min ?? null} disabled={readOnly} placeholder="min"
                         suffix={split && unit ? unit : undefined}
                         data-mon="min" onChange={(v) => patch({ min: v })} />
            {split ? null : <span className="u0-mon-dash">–</span>}
            <InputNumber size="small" value={m.max ?? null} disabled={readOnly} placeholder="max"
                         suffix={split && unit ? unit : undefined}
                         data-mon="max" onChange={(v) => patch({ max: v })} />
          </div>
        </Field>
        <Field label="Reading schedule" hint="Periodic is gated by the server at each write: a reading is due every interval from the operation's start. Count asks for a fixed number. Ad hoc is never gated.">
          <Select size="small" value={schedule} disabled={readOnly} data-mon="schedule"
                  onChange={(v) => patch({ schedule: v })} options={words.schedules} />
        </Field>
        {schedule === "Periodic" ? (
          <Field label="Every" hint="The interval between readings." required={!readOnly}>
            <div className="u0-mon-pair">
              <InputNumber size="small" min={1} value={m.every ?? null} disabled={readOnly}
                           data-mon="every" onChange={(v) => patch({ every: v })} status={showErrors && !m.every ? "error" : undefined} />
              <Select size="small" value={m.every_unit || "min"} disabled={readOnly}
                      onChange={(v) => patch({ every_unit: v })} options={words.interval_units} />
            </div>
          </Field>
        ) : null}
        {schedule !== "Adhoc" ? (
          <Field label={schedule === "Count" ? "Number of readings" : "Readings expected"} required={!readOnly && schedule === "Count"}
                 hint={schedule === "Count" ? "How many readings the operator takes." : "How many readings the operation expects at this interval — informative; the interval is what gates."}>
            <InputNumber size="small" min={1} value={m.readings ?? null} disabled={readOnly}
                         data-mon="readings" onChange={(v) => patch({ readings: v })}
                         status={showErrors && schedule === "Count" && !m.readings ? "error" : undefined} />
          </Field>
        ) : null}
        <Field label="Out of range" hint="What a reading outside the range means. Correct and re-check lets the operator act and read again; an IPC failure or an OOS raises a quality item.">
          <Select size="small" value={m.on_out_of_range || undefined} disabled={readOnly} data-mon="on_out_of_range"
                  placeholder="— choose —" onChange={(v) => patch({ on_out_of_range: v })} options={words.out_of_range} />
        </Field>
        {m.on_out_of_range === "CORRECT" ? (
          <Field label="Correction attempts" hint="How many times the operator may correct and re-read before a rework sheet is required." required={!readOnly}>
            <InputNumber size="small" min={1} value={m.attempts ?? null} disabled={readOnly}
                         data-mon="attempts" onChange={(v) => patch({ attempts: v })}
                         status={showErrors && !m.attempts ? "error" : undefined} />
          </Field>
        ) : null}
      </div>
      <MonitoringSettings m={m} readOnly={readOnly} patch={patch} unit={unit} areas={step.areas || []}
                          part={split ? (adv ? "all" : "core") : "all"} />
      {split ? (
        <button type="button" className="u0-advlink" onClick={() => setAdv(!adv)} aria-expanded={adv}>
          Advanced · {adv ? "hide" : "expected value, capture, deviation rule, grace and escalation"}
        </button>
      ) : null}
      {split && !adv ? null : <div className="u0-mon-flags">
        <label className="u0-mon-flag">
          <input type="checkbox" checked={!!m.continuous} disabled={readOnly} data-mon="continuous"
                 onChange={(e) => patch({ continuous: e.target.checked })} />
          <span>Continuous — watched throughout, not read at moments</span>
        </label>
        {/* Critical parameter (CPP) is set under "Rules bound here", with the runtime rules. */}
      </div>}
      {m.code ? <span className="u0-mon-code">row {m.code}</span> : null}
    </div>
  );
}

/**
 * 2026-10-06 · the rest of the /process-config Monitoring dialog (mbmr/Monitoring.js), on the
 * step: execution and capture mode, deviation rule, state duration, expected value, quantity,
 * grace window, escalation delay, trigger, start / end and the integration service. Saved on
 * the step's monitoring row — the same Process_Parameter columns that dialog writes.
 */
const MON_MODES = ["Manual", "Semi-Automatic", "Automatic"];
const MON_CAPTURE = { Manual: ["Manual", "Photo/OCR", "Integration Assisted"], "Semi-Automatic": ["Photo/OCR", "Integration Assisted"], Automatic: ["Integration Assisted"] };
export const MON_DEVIATION = [
  { value: "none", label: "None" }, { value: "less_than_min", label: "Less than min" },
  { value: "greater_than_max", label: "Greater than max" }, { value: "less_than_min_or_greater_than_max", label: "Less than min or greater than max" },
];
const MON_TIME_UNITS = [{ value: "sec", label: "sec" }, { value: "min", label: "min" }, { value: "hrs", label: "hrs" }];
const MON_RULES = [
  ["allow_dynamic_grouping", "Allow dynamic grouping"], ["auto_include_in_execution_round", "Auto include in execution round"],
  ["allow_partial_submission", "Allow partial submission"],
];

/** The step's monitoring, or — before it has a row — the Monitoring dialog's defaults, shown so
 *  the author starts from them; nothing is written until one is changed. */
export const MON_DEFAULTS = {
  schedule: "Periodic", mode: "Manual", capture_modes: ["Manual"], track_reading: true, track_duration: false,
  deviation_rule: "none", grace_value: 1, grace_unit: "min", escalation_value: 2, escalation_unit: "min",
  duration_unit: "min", trigger_type: "User", is_start: false, is_end: false, critical: false,
  rules: { allow_dynamic_grouping: true, auto_include_in_execution_round: true, allow_partial_submission: false },
};
export function monitoringOf(step) {
  return step.monitoring || MON_DEFAULTS;
}

export function MonitoringSettings({ m, readOnly, patch, unit, areas = [], part = "all" }) {
  const mode = m.mode || "Manual";
  const allowed = MON_CAPTURE[mode] || MON_CAPTURE.Manual;
  const capture = (m.capture_modes || []).filter((c) => allowed.includes(c));
  const pair = (valueKey, unitKey, placeholder) => (
    <div className="u0-mon-pair">
      <InputNumber size="small" min={0} value={m[valueKey] ?? null} disabled={readOnly} placeholder={placeholder}
                   onChange={(v) => patch({ [valueKey]: v })} />
      <Select size="small" value={m[unitKey] || "min"} disabled={readOnly} options={MON_TIME_UNITS}
              onChange={(v) => patch({ [unitKey]: v })} style={{ width: 72 }} />
    </div>
  );
  return (
    <div className="u0-mon-grid" data-testid="monitoring-settings">
      {/* The dialog's Target: the machines the reading is taken on, or a location. */}
      <Field label="Target">
        <Select size="small" value={m.target_type || "Machine"} disabled={readOnly}
                options={[{ value: "Machine", label: "Machine" }, { value: "Location", label: "Location" }]}
                onChange={(v) => patch({ target_type: v })} />
      </Field>
      {(m.target_type || "Machine") === "Machine" ? (
        <Field label="Machine" hint="Machine category → group → machines, as /process-config's Monitoring offers them.">
          <MachineSidePicker value={m.machine} readOnly={readOnly} onChange={(v) => patch({ machine: v })} />
        </Field>
      ) : (
        <Field label="Location">
          <Select size="small" allowClear disabled={readOnly} value={m.location_area || undefined} placeholder="— area —"
                  options={areas.map((a) => ({ value: a.name || a.code, label: a.name || a.code }))}
                  notFoundContent='Choose "May run in" for the step first.'
                  onChange={(v) => patch({ location_area: v || "" })} />
        </Field>
      )}
      {part === "core" ? null : <>
      <Field label="Execution mode">
        <Select size="small" value={mode} disabled={readOnly} options={MON_MODES.map((v) => ({ value: v, label: v === "Semi-Automatic" ? "Semi-Auto" : v }))}
                onChange={(v) => {
                  const keep = (m.capture_modes || []).filter((c) => (MON_CAPTURE[v] || []).includes(c));
                  patch({ mode: v, capture_modes: keep.length ? keep : [(MON_CAPTURE[v] || ["Manual"])[0]],
                          ...(v === "Automatic" && m.schedule === "Adhoc" ? { schedule: "Periodic" } : {}) });
                }} />
      </Field>
      <Field label="Capture mode">
        <Select size="small" mode="multiple" value={capture} disabled={readOnly}
                options={allowed.map((v) => ({ value: v, label: v }))}
                onChange={(v) => patch({ capture_modes: v })} placeholder="— choose —" />
      </Field>
      {capture.includes("Integration Assisted") ? (
        <Field label="Integration service">
          <Input size="small" value={m.integration_service || ""} disabled={readOnly} placeholder="service type"
                 onChange={(e) => patch({ integration_service: e.target.value })} />
        </Field>
      ) : null}
      <Field label="Deviation rule" hint="Which side of the range raises a deviation.">
        <Select size="small" value={m.deviation_rule || "none"} disabled={readOnly} options={MON_DEVIATION}
                onChange={(v) => patch({ deviation_rule: v })} />
      </Field>
      <Field label={`Quantity${unit ? ` (${unit})` : ""}`}>
        <InputNumber size="small" min={0} value={m.quantity ?? null} disabled={readOnly} style={{ width: "100%" }}
                     placeholder="—" onChange={(v) => patch({ quantity: v })} />
      </Field>
      <Field label="Expected value" hint="For a yes / no parameter: the value a reading should show.">
        <Select size="small" allowClear disabled={readOnly} placeholder="— not set —"
                value={m.expected_bool == null ? undefined : String(m.expected_bool)}
                options={[{ value: "true", label: "True" }, { value: "false", label: "False" }]}
                onChange={(v) => patch({ expected_bool: v == null ? null : v === "true" })} />
      </Field>
      <Field label="Capture parameter value">
        <div><Switch size="small" checked={m.track_reading !== false} disabled={readOnly || !!m.track_duration}
                     checkedChildren="Yes" unCheckedChildren="No" onChange={(v) => patch({ track_reading: v })} /></div>
      </Field>
      <Field label="Track state duration">
        <div><Switch size="small" checked={!!m.track_duration} disabled={readOnly} checkedChildren="Yes" unCheckedChildren="No"
                     onChange={(v) => patch({ track_duration: v, ...(v ? { track_reading: true } : {}) })} /></div>
      </Field>
      {m.track_duration ? (
        <Field label="Duration (min – max)">
          <div className="u0-mon-pair">
            <InputNumber size="small" min={0} value={m.duration_min ?? null} disabled={readOnly} placeholder="min"
                         onChange={(v) => patch({ duration_min: v })} />
            <span className="u0-mon-dash">–</span>
            <InputNumber size="small" min={0} value={m.duration_max ?? null} disabled={readOnly} placeholder="max"
                         onChange={(v) => patch({ duration_max: v })} />
            <Select size="small" value={m.duration_unit || "min"} disabled={readOnly} options={MON_TIME_UNITS}
                    onChange={(v) => patch({ duration_unit: v })} style={{ width: 72 }} />
          </div>
        </Field>
      ) : null}
      <Field label="Grace window" hint="± how late or early a reading may be taken.">{pair("grace_value", "grace_unit", "1")}</Field>
      {m.schedule !== "Adhoc" ? (
        <Field label="Escalation delay" hint="How long after a missed reading it is escalated.">{pair("escalation_value", "escalation_unit", "2")}</Field>
      ) : (
        <Field label="Trigger type">
          <Select size="small" value={m.trigger_type || "User"} disabled={readOnly}
                  options={[{ value: "User", label: "User" }, { value: "Event Triggered", label: "Event triggered" }]}
                  onChange={(v) => patch({ trigger_type: v })} />
        </Field>
      )}
      <Field label="Is start">
        <div><Switch size="small" checked={!!m.is_start} disabled={readOnly} checkedChildren="Yes" unCheckedChildren="No"
                     onChange={(v) => patch({ is_start: v })} /></div>
      </Field>
      <Field label="Is end">
        <div><Switch size="small" checked={!!m.is_end} disabled={readOnly} checkedChildren="Yes" unCheckedChildren="No"
                     onChange={(v) => patch({ is_end: v })} /></div>
      </Field>
      </>}
    </div>
  );
}

/** The Monitoring dialog's Runtime Execution Rules, under "Rules bound here". Critical
 *  parameter (CPP) is the editor's own critical flag. */
export function MonitoringRules({ step, readOnly, onStage, hideCritical = false }) {
  const m = step.monitoring;
  if (!m) return null;
  const rules = m.rules || {};
  return (
    <div className="u0-ingbox" style={{ marginBottom: 10 }} data-testid="monitoring-rules">
      <span className="u0-eyebrow">Runtime execution rules</span>
      <div className="u0-ingsws">
        {MON_RULES.map(([k, label]) => (
          <label key={k} className="u0-ingsw">
            <span>{label}</span>
            <Switch size="small" disabled={readOnly} checked={!!rules[k]} checkedChildren="Yes" unCheckedChildren="No"
                    onChange={(v) => onStage({ monitoring: { ...m, rules: { ...rules, [k]: v } } })} />
          </label>
        ))}
        {/* 2026-10-14 · under "Track in CPV" in the redesigned step panel; here only where CPV is not. */}
        {hideCritical ? null : <label className="u0-ingsw">
          <span>Critical parameter (CPP)</span>
          <Switch size="small" disabled={readOnly} checked={!!m.critical} checkedChildren="Yes" unCheckedChildren="No"
                  onChange={(v) => onStage({ monitoring: { ...m, critical: v } })} />
        </label>}
      </div>
    </div>
  );
}
